import React, { useState, useEffect, useMemo } from 'react';
import { useTranslations } from 'use-intl';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Button from '@/UIlibrary/Button/Button';
import { BRAND_COLORS, GRAY_COLORS } from '@/UIlibrary/globalTheme';
import { Calendar } from 'react-date-range';
import { es } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Calendar as CalendarIcon } from 'react-feather';
import { Maybe, Request } from '@/graphql/__generated__/van-credits';

interface VanCreditCreateFormProps {
	requestData: Maybe<Request> | undefined;
	isEditMode: boolean;
	onSubmit: (date: Date) => void;
	onDateChange?: (date: Date | null) => void;
	loading: boolean;
	firstPaymentDate: string | undefined;
	loadingFirstPayment: boolean;
	onCancel?: () => void;
}

const VanCreditCreateForm: React.FC<VanCreditCreateFormProps> = ({
	requestData,
	isEditMode,
	onSubmit,
	onDateChange,
	loading,
	firstPaymentDate,
	loadingFirstPayment,
	onCancel
}) => {
	const t = useTranslations('CreateVanCredit');

	const [expectedDisbursementDate, setExpectedDisbursementDate] = useState<Date | null>(() => {
		if (isEditMode && requestData?.expectedDisbursementDate) {
			return new Date(requestData.expectedDisbursementDate + 'T00:00:00');
		}
		return null;
	});
	const [showDatePicker, setShowDatePicker] = useState(false);

	const simulation = requestData?.simulation;
	const simulationConfig = simulation?.configuration;

	const isWeekend = (date: Date): boolean => {
		const day = date.getDay();
		return day === 0 || day === 6;
	};

	const minDate = useMemo(() => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		let weekdaysCount = 0;
		const currentDate = new Date(today);

		while (weekdaysCount < 7) {
			currentDate.setDate(currentDate.getDate() + 1);
			if (!isWeekend(currentDate)) {
				weekdaysCount++;
			}
		}

		return currentDate;
	}, []);

	const formatDateForDisplay = (date: Date | null): string => {
		if (!date) return t('SelectDate');
		return date.toLocaleDateString('es-MX', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};

	const formatFirstPaymentDate = (dateString: string | null | undefined): string => {
		if (!dateString) return '-';
		const date = new Date(dateString + 'T00:00:00');
		return date.toLocaleDateString('es-MX', {
			day: 'numeric',
			month: 'long',
			year: 'numeric'
		});
	};

	const formatDisplayValue = (value: string | number | null | undefined): string => {
		if (value === null || value === undefined) return '-';
		return String(value);
	};

	const formatBooleanValue = (value: boolean | null | undefined): string => {
		if (value === null || value === undefined) return '-';
		return value ? t('Yes') : t('No');
	};

	const renderReadOnlyField = (label: string, value: string) => (
		<div className="flex flex-row justify-between py-2 border-b border-gray-200">
			<Typography size="sm" weight="medium">
				{label}
			</Typography>
			<Typography size="sm" color="subtitle">
				{value}
			</Typography>
		</div>
	);

	const handleDateSelect = (date: Date) => {
		setExpectedDisbursementDate(date);
		setShowDatePicker(false);
		if (onDateChange) {
			onDateChange(date);
		}
	};

	const handleSubmit = () => {
		if (!expectedDisbursementDate) {
			return;
		}
		onSubmit(expectedDisbursementDate);
	};

	useEffect(() => {
		if (isEditMode && requestData?.expectedDisbursementDate) {
			setExpectedDisbursementDate(new Date(requestData.expectedDisbursementDate + 'T00:00:00'));
		}
	}, [isEditMode, requestData?.expectedDisbursementDate]);

	return (
		<div
			className="flex flex-col p-6 gap-5 rounded-lg mt-5"
			style={{ backgroundColor: GRAY_COLORS.gray50 }}
		>
			<Typography size="md" weight="semibold">
				{t(isEditMode ? 'EditCreditDetails' : 'SimulationDetails')}
			</Typography>

			<div className="bg-white rounded-lg p-4">
				<div className="flex flex-col gap-2">
					{renderReadOnlyField(
						t('PaymentCapacity'),
						formatDisplayValue(simulationConfig?.paymentCapacity)
					)}
					{renderReadOnlyField(
						t('AmountRequested'),
						formatDisplayValue(simulationConfig?.amountRequested)
					)}
					{renderReadOnlyField(
						t('OrdinaryInterestRate'),
						formatDisplayValue(simulationConfig?.ordinaryInterestRate)
					)}
					{renderReadOnlyField(
						t('DaysPerPeriod'),
						formatDisplayValue(simulationConfig?.daysPerPeriod)
					)}
					{renderReadOnlyField(
						t('SameDayPayment'),
						formatBooleanValue(simulationConfig?.sameDayPayment)
					)}
					{renderReadOnlyField(
						t('NumberOfPeriods'),
						formatDisplayValue(simulation?.numberOfPeriods)
					)}
				</div>
			</div>

			<div className="bg-white rounded-lg p-4">
				<div className="flex flex-col gap-4">
					<div className="flex flex-row items-center gap-4">
						<div className="w-1/2">
							<div className="mb-2">
								<Typography size="sm" weight="medium">
									{t('ExpectedDisbursementDate')}
								</Typography>
							</div>
							<div className="relative">
								<Button
									onClick={() => setShowDatePicker(!showDatePicker)}
									size="auto"
									color="white"
									outlined
									endIcon={<CalendarIcon size={20} />}
									label={formatDateForDisplay(expectedDisbursementDate)}
								/>
								{showDatePicker && (
									<div className="absolute z-50 mt-2 bg-white shadow-lg rounded-lg">
										<Calendar
											date={expectedDisbursementDate || minDate}
											onChange={handleDateSelect}
											locale={es}
											minDate={minDate}
											color={BRAND_COLORS.brand600}
											disabledDay={isWeekend}
										/>
									</div>
								)}
							</div>
						</div>
						<div className="w-1/2 flex flex-col">
							<Typography size="sm" weight="medium">
								{t('FirstPaymentDate')}
							</Typography>
							<Typography size="md" weight="medium" color="subtitle">
								{loadingFirstPayment
									? t('CalculatingFirstPayment')
									: formatFirstPaymentDate(firstPaymentDate)}
							</Typography>
						</div>
					</div>
				</div>
			</div>

			<div className="flex justify-end gap-3">
				{isEditMode && onCancel && (
					<div className="w-60">
						<Button
							label={t('Cancel')}
							onClick={onCancel}
							size="full"
							color="white"
							outlined
							disabled={loading}
						/>
					</div>
				)}
				<div className="w-60">
					<Button
						label={t(isEditMode ? 'ModifyCredit' : 'CreateCredit')}
						onClick={handleSubmit}
						size="full"
						color="primary"
						loading={loading}
						disabled={!expectedDisbursementDate || loading}
					/>
				</div>
			</div>
		</div>
	);
};

export default VanCreditCreateForm;
